import React from 'react';
import { useTranslations } from 'use-intl';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Button from '@/UIlibrary/Button/Button';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';
import { formatDate, formatMoney, formatPercentageValue } from '@/utils/utils';
import { Maybe, Request } from '@/graphql/__generated__/van-credits';

interface VanCreditReadOnlyProps {
	requestData: Maybe<Request> | undefined;
	canEdit: boolean;
	onEdit?: () => void;
}

const VanCreditReadOnly: React.FC<VanCreditReadOnlyProps> = ({ requestData, canEdit, onEdit }) => {
	const t = useTranslations('CreateVanCredit');

	const formatDisplayValue = (value: string | number | null | undefined): string => {
		if (value === null || value === undefined) return '-';
		return String(value);
	};

	const renderReadOnlyField = (label: string, value: string) => (
		<div className="flex flex-row justify-between py-2 border-b border-gray-200">
			<Typography size="sm" weight="medium">
				{label}
			</Typography>
			<Typography size="sm" color="subtitle">
				{value}
			</Typography>
		</div>
	);

	return (
		<div
			className="flex flex-col p-6 gap-5 rounded-lg mt-5"
			style={{ backgroundColor: GRAY_COLORS.gray50 }}
		>
			<div className="flex justify-between items-center">
				<Typography size="md" weight="semibold">
					{t('CreditDetails')}
				</Typography>
				{canEdit && onEdit && (
					<Button label={t('ModifyCredit')} onClick={onEdit} size="auto" color="primary" outlined />
				)}
			</div>

			<div className="bg-white rounded-lg p-4">
				<div className="flex flex-col gap-2">
					{renderReadOnlyField(t('OpenfinId'), formatDisplayValue(requestData?.openfinIdasociado))}
					{renderReadOnlyField(
						t('OpenFinCreditId'),
						formatDisplayValue(requestData?.openfinCuenta)
					)}
					{renderReadOnlyField(
						t('CreditAmountRequested'),
						formatMoney(requestData?.creditAccountDetails?.amountRequested)
					)}
					{renderReadOnlyField(
						t('OrdinaryInterestRate'),
						formatPercentageValue(requestData?.creditAccountDetails?.ordinaryInterestRate || '0')
					)}
					{renderReadOnlyField(
						t('TiieBanxico'),
						formatDisplayValue(requestData?.creditAccountDetails?.tiieValue)
					)}
					{renderReadOnlyField(
						t('TiieDate'),
						formatDate(requestData?.creditAccountDetails?.tiieDate || '')
					)}

					{renderReadOnlyField(
						t('BasisPoints'),
						formatPercentageValue(requestData?.creditAccountDetails?.basisPoints || '0')
					)}
					{renderReadOnlyField(
						t('DaysPerPeriod'),
						formatDisplayValue(requestData?.creditAccountDetails?.daysPerPeriod)
					)}
					{renderReadOnlyField(
						t('NumberOfPeriods'),
						formatDisplayValue(requestData?.creditAccountDetails?.numberOfPeriods)
					)}
					{renderReadOnlyField(
						t('SameDayPayment'),
						requestData?.creditAccountDetails?.sameDayPayment ? t('Yes') : t('No')
					)}
					{renderReadOnlyField(
						t('FirstPaymentDate'),
						formatDate(requestData?.creditAccountDetails?.firstPaymentDate || '')
					)}

					{renderReadOnlyField(
						t('InsuranceAmount'),
						formatMoney(requestData?.creditAccountDetails?.insuranceAmount)
					)}
					{renderReadOnlyField(
						t('GpsAmount'),
						formatMoney(requestData?.creditAccountDetails?.gpsAmount)
					)}
				</div>
			</div>
		</div>
	);
};

export default VanCreditReadOnly;
